import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { equipmentTemplatesAPI } from "../api/api";
import { LoadingSpinner, EmptyState, Modal } from "../components/Common";
import { useSearch } from "../context/SearchContext";
import { useLanguage } from "../context/LanguageContext";
import { printTable } from "../utils/printTable";

// Simple, filterable table of stock for buildable component templates
// (e.g. how many finished LMTs are ready). It's a plain signed counter --
// can be negative if more were consumed by parent builds than were ever
// produced -- and can be corrected directly here. Editing it only ever
// writes this counter, never parts/equipment_parts, so raw-parts stock is
// untouched.
const ComponentStock = () => {
  const { searchTerm } = useSearch();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [historyRow, setHistoryRow] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await equipmentTemplatesAPI.getComponentsStock();
      setRows(response.data.data);
    } catch (err) {
      alert(t("componentStock.failedToLoad", { error: err.response?.data?.error || err.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = [...new Set(rows.map((r) => r.category).filter(Boolean))].sort();
  const brands = [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort();
  const hasActiveFilters = filterCategory || filterBrand;

  const clearFilters = () => {
    setFilterCategory("");
    setFilterBrand("");
  };

  const filtered = rows.filter((r) => {
    const matchesSearch =
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || r.category === filterCategory;
    const matchesBrand = !filterBrand || r.brand === filterBrand;
    return matchesSearch && matchesCategory && matchesBrand;
  });

  const startEdit = (row) => {
    setEditingId(row.template_id);
    setEditValue(String(row.component_stock));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (row) => {
    const quantity = parseInt(editValue, 10);
    if (Number.isNaN(quantity)) return;
    setSavingId(row.template_id);
    try {
      const response = await equipmentTemplatesAPI.setComponentReadyStock(
        row.template_id,
        quantity,
      );
      const updated = response.data.data;
      setRows((prev) =>
        prev.map((r) =>
          r.template_id === row.template_id
            ? { ...r, component_stock: updated.component_stock }
            : r,
        ),
      );
      setEditingId(null);
    } catch (err) {
      alert(t("componentStock.failedToUpdate", { error: err.response?.data?.error || err.message }));
    } finally {
      setSavingId(null);
    }
  };

  const openHistory = async (row) => {
    setHistoryRow(row);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const response = await equipmentTemplatesAPI.getComponentStockMovements(
        row.template_id,
      );
      setHistory(response.data.data);
    } catch (err) {
      alert(t("componentStock.failedToLoadHistory", { error: err.response?.data?.error || err.message }));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePrint = () => {
    printTable({
      title: t("componentStock.title"),
      subtitle: [filterCategory, filterBrand].filter(Boolean).join(", "),
      columns: [
        { header: t("common.name") },
        { header: t("common.category") },
        { header: t("equipmentTemplates.brand") },
        { header: t("componentStock.ready"), align: "right" },
      ],
      rows: filtered.map((r) => [r.name, r.category, r.brand, r.component_stock]),
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t("componentStock.title")}
          </h1>
          <p className="text-gray-500">{t("componentStock.subtitle")}</p>
        </div>
        <button
          onClick={handlePrint}
          className="btn-secondary"
          disabled={filtered.length === 0}
        >
          🖨️ {t("common.print")}
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 gap-4">
          <div className="flex-1">
            {searchTerm && (
              <p className="text-sm text-gray-600">
                {t("componentStock.searchingFor")}{" "}
                <span className="font-medium text-gray-900">"{searchTerm}"</span>
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input-field w-auto"
            >
              <option value="">{t("componentStock.allCategories")}</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="input-field w-auto"
            >
              <option value="">{t("componentStock.allBrands")}</option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn-secondary whitespace-nowrap">
                {t("common.clearFilters")}
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          hasItems={rows.length > 0}
          icon="📦"
          title={t("componentStock.emptyTitle")}
          message={t("componentStock.emptyMessage")}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("common.name")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("common.category")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("equipmentTemplates.brand")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("componentStock.ready")}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((row) => (
                <tr key={row.template_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {row.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {row.category || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {row.brand || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {editingId === row.template_id ? (
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="input-field w-20 text-center"
                        autoFocus
                      />
                    ) : (
                      <span
                        className={`text-lg font-bold ${
                          row.component_stock < 0 ? "text-red-600" : "text-purple-600"
                        }`}
                      >
                        {row.component_stock}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right space-x-3">
                    {editingId === row.template_id ? (
                      <>
                        <button
                          onClick={() => saveEdit(row)}
                          disabled={savingId === row.template_id}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        >
                          {savingId === row.template_id
                            ? t("common.saving")
                            : t("common.save")}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          {t("common.cancel")}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() =>
                            navigate(`/production-tasks?new=component&id=${row.template_id}`)
                          }
                          className="text-orange-600 hover:text-orange-900"
                        >
                          📝 {t("productionTasks.sendToProduce")}
                        </button>
                        <button
                          onClick={() => openHistory(row)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          {t("componentStock.history")}
                        </button>
                        <button
                          onClick={() => startEdit(row)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {t("common.edit")}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={!!historyRow}
        onClose={() => setHistoryRow(null)}
        title={t("componentStock.historyTitle", { name: historyRow?.name })}
        size="lg"
      >
        {historyLoading ? (
          <LoadingSpinner />
        ) : history.length === 0 ? (
          <p className="text-gray-500 text-sm">{t("componentStock.historyEmpty")}</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("componentStock.colDate")}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("componentStock.colType")}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  {t("componentStock.colChange")}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  {t("componentStock.colBeforeAfter")}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("componentStock.colUser")}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("componentStock.colNotes")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {history.map((m) => (
                <tr key={m.id}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                    {new Date(m.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-800">
                    {t(`componentStock.type_${m.movement_type}`)}
                  </td>
                  <td
                    className={`px-3 py-2 text-center font-semibold ${
                      m.quantity_change < 0 ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {m.quantity_change > 0 ? `+${m.quantity_change}` : m.quantity_change}
                  </td>
                  <td className="px-3 py-2 text-center whitespace-nowrap text-gray-700">
                    {m.stock_before} → {m.stock_after}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                    {m.user_name || "-"}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{m.notes || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  );
};

export default ComponentStock;
